/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../locales/en-US/descriptors';
import IEnergyManagementProps, {
  EUtilityConfig,
  ECartAt,
  IAny
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import UtilityBillPerformanceDetailComponent from '../UtilityBillPerformanceDetailWidget/UtilityBillPerformanceDetailWidget';
import SavingsByMeterComponent from '../SavingsByMeterWidget/SavingsByMeterComponent';
import useConnectorAndDynamicProps from '../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import useMounted from '../../shared/useMount/useMount';
import {
  excludeCurrentMonth,
  formattedDate,
  getEndDateForUtility,
  getFirstDayofSelectedMonth
} from '../../shared/Utils/Utils';
import { UtilityType } from '../../shared/Utils/Constants';
import { getConnctorConfig } from '../../shared/Utils/DataConnectorUtils';
import { ApiContext } from '../../../context/ApiContext';
import UtilitySummaryWidget from './UtilitySummary/UtilitySummaryWidget';
// import UtilityBillBreakdownChartWidget from './BillBreakdownWidgets/UtilityBillBreakdownChartWidget';
import {
  Button,
  Icon,
  Tooltip,
  Notification,
  Card,
  Loader
} from '@scuf/common';
import './UtilitySavingDetailsHOC.scss';
import { getUtilityTitle } from './UtilityBillViewDetailsUtils';
import moment from 'moment';
// import CommonUtilitySummaryTable from './UtilitySummary/CommonUtilitySummaryTable';
import { EmaasSubscriptionType } from '../../shared/Enums';
import UtilityAccountDropdown from './UtilityAccountDropdown/UtilityAccountDropdownComponent';
import UtilityBillBreakdownChartWidgetTogglePerformance from './BillBreakdownWidgets/UtilityBillBreakdownChartWidgetTogglePerformance';
import UtilitySummaryWidgetPerformance from './UtilitySummary/UtilitySummaryWidgetPerformance';

const portfolioUtilityConfig = [
  EUtilityConfig.electricityUtilityPortfolioLabel,
  EUtilityConfig.gasUtilityPortfolioLabel,
  EUtilityConfig.waterUtilityPortfolioLabel,
  EUtilityConfig.seweragePortfolioLabel
];

export const siteLabelUtilityConfig = [
  EUtilityConfig.electricityUtilitySiteLabel,
  EUtilityConfig.gasUtilitySiteLabel,
  EUtilityConfig.waterUtilitySiteLabel,
  EUtilityConfig.sewerageUtilitySiteLabel
];

const selectedUtility = [
  UtilityType.Electricity,
  UtilityType.Gas,
  UtilityType.Water,
  UtilityType.Sewerage
];
interface IButtongroup {
  content: string;
  type:
    | 'link'
    | 'primary'
    | 'secondary'
    | 'inline'
    | 'inline-secondary'
    | undefined;
}
export interface IEnergyManagementHOCProps extends IEnergyManagementProps {
  ActiveIndex: number;
  utilityType: string;
}

const UtilitySavingDetailHOCTogglePerformance = (
  props: IEnergyManagementHOCProps
) => {
  const [t] = useTranslation();
  const apiContext = useContext(ApiContext);
  const d = descriptors.energyManagement;
  const {
    bearerToken,
    requestContext,
    ActiveIndex,
    utilityType,
    emaasContext
  } = props;

  const toggleUtilityBillPerformance =
    props.emaasContext?.toggleUtilityBillBreakdownPerformance;

  const [isOutOfRange, setIsOutOfRange] = useState<boolean>(false);

  const [buttonGroup, setButtonGroup] = useState<IButtongroup[]>(
    apiContext.basicAdvancedGrp
      ? apiContext.basicAdvancedGrp
      : [
          { content: t(d.BASIC), type: 'primary' },
          { content: t(d.ADVANCED), type: 'secondary' }
        ]
  );

  const [buttonGroupBilling, setButtonGroupBilling] = useState<IButtongroup[]>(
    apiContext.monthlyBillingGrp
      ? apiContext.monthlyBillingGrp
      : [
          { content: 'Monthly', type: 'primary' },
          { content: 'Billing', type: 'secondary' }
        ]
  );
  const activeTab = ActiveIndex - 1;
  const UtilityBillType = selectedUtility[activeTab];

  const [isOverview, setIsOverview] = useState(apiContext.overViewElectricity);
  const [isMonthly, setIsMonthly] = useState<IAny>(
    apiContext.isMonthlyElectricity
  );
  const [selectedAccountNumber, setSelectedAccountNumber] = useState<string[]>(
    []
  );

  const [graphData, setGraphData] = useState<any>({});
  const onBackarrowClick = () => setIsOverview(true);
  const onViewDetailsClick = () => {
    apiContext.overviewSet(false);
    setIsOverview(false);
  };

  const [prevRContext, setPrevRContext] = useState<IAny>(requestContext);
  const [prevRContextAdvnc, setPrevRContextAdvnc] = useState<IAny>(
    requestContext
  );

  const hasRContextChanged = (request: IAny) => {
    return (
      request.context.startTime !== prevRContext.context.startTime ||
      request.context.endTime !== prevRContext.context.endTime ||
      requestContext.context.nodeId !== prevRContext.context.nodeId
    );
  };

  // ||
  //     requestContext.context.isCashFlow !== prevRContextAdvnc.context.isCashFlow
  const hasRContextChangedForAdvnc = (request: IAny) => {
    return (
      request.context.startTime !== prevRContextAdvnc.context.startTime ||
      request.context.endTime !== prevRContextAdvnc.context.endTime ||
      requestContext.context.nodeId !== prevRContextAdvnc.context.nodeId
    );
  };

  const toggleButtonForMonthlyBilling = (
    clickedButtonIndex: number,
    buttonData: IButtongroup
  ) => {
    const updatedButtons: IButtongroup[] = buttonGroupBilling.map(
      (button: IButtongroup, index: number) => {
        if (clickedButtonIndex === index) {
          return { ...button, type: 'primary' };
        } else {
          return { ...button, type: 'secondary' };
        }
      }
    );

    if (clickedButtonIndex === 0) {
      setIsMonthly(true);
      apiContext.setIsMonthlyElectricity(true);
    } else {
      setIsMonthly(false);
      apiContext.setIsMonthlyElectricity(false);
    }

    apiContext &&
      apiContext.setMonthlyBillingGrp &&
      apiContext.setMonthlyBillingGrp(updatedButtons);

    setButtonGroupBilling(updatedButtons);
  };

  useEffect(() => {
    if (isOverview) {
      setButtonGroupBilling([
        { content: 'Monthly', type: 'primary' },
        { content: 'Billing', type: 'secondary' }
      ]);
      apiContext.setIsMonthlyElectricity(true);
      setIsMonthly(true);
    }
  }, [isOverview]);

  useEffect(() => {
    apiContext.onViewChange && apiContext.onViewChange(isMonthly);
  }, [isMonthly]);

  const toggleButton = (
    clickedButtonIndex: number,
    buttonData: IButtongroup
  ) => {
    const updatedButtons: IButtongroup[] = buttonGroup.map(
      (button: IButtongroup, index: number) => {
        if (clickedButtonIndex === index) {
          setIsOverview(false);
          apiContext.overviewSet(false);
          return { ...button, type: 'primary' };
        } else {
          setIsOverview(true);
          apiContext && apiContext.overviewSet && apiContext.overviewSet(true);
          return { ...button, type: 'secondary' };
        }
      }
    );
    apiContext &&
      apiContext.setBasicAdvancedGrp &&
      apiContext.setBasicAdvancedGrp(updatedButtons);
    setButtonGroup(updatedButtons);
  };

  const startTime = getFirstDayofSelectedMonth(
    requestContext.context.startTime!
  );

  const startTimeForBreakdown = moment(
    requestContext.context.startTime!
  ).format('YYYY-MM-DD');
  const endTime =
    requestContext.context.subscriptionType ===
    EmaasSubscriptionType.PREMIUM_SUBSCRIPTION
      ? getEndDateForUtility(
          requestContext.context.endTime!,
          requestContext.context.startTime!
        )
      : excludeCurrentMonth(
          requestContext.context.endTime!,
          requestContext.context.startTime!
        );
  const endTimeNewFormat = formattedDate(endTime);

  const endTimeForBreakdown = moment(requestContext.context.endTime!).format(
    'YYYY-MM-DD'
  );

  const configName =
    apiContext.apiCallAt === ECartAt.portfolioLabel
      ? portfolioUtilityConfig[activeTab]
      : siteLabelUtilityConfig[activeTab];
  const connectorConfig = getConnctorConfig(
    configName,
    requestContext,
    startTime,
    endTimeNewFormat
  );

  const isMounted = useMounted();

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  const isDateRangeValid = () => {
    const sDate = moment(
      getFirstDayofSelectedMonth(requestContext.context.startTime!),
      'YYYY-MM-DD'
    );
    const eDate =
      requestContext.context.subscriptionType ===
      EmaasSubscriptionType.PREMIUM_SUBSCRIPTION
        ? moment(
            getEndDateForUtility(
              requestContext.context.endTime!,
              requestContext.context.startTime!
            ),
            'YYYY-MM-DD'
          )
        : moment(
            excludeCurrentMonth(
              requestContext.context.endTime!,
              requestContext.context.startTime!
            ),
            'YYYY-MM-DD'
          );
    return eDate.diff(sDate, 'days') <= 365;
  };

  useEffect(() => {
    if (isMounted && hasRContextChanged(requestContext)) {
      if (isDateRangeValid()) {
        setIsOutOfRange(false);

        setPrevRContext(requestContext);
        dataConnector &&
          dataConnector.actions &&
          dataConnector.actions.getAll();
      } else {
        setIsOutOfRange(true);
      }
    }
  }, [requestContext]);

  useEffect(() => {
    !isDateRangeValid() && setIsOutOfRange(true);
  }, []);

  const getGraphData = async (selectedAccountNumber: any) => {
    let cashFlow;
    if (isMonthly === false) {
      cashFlow = true;
    } else cashFlow = false;

    if (Array.isArray(selectedAccountNumber) && selectedAccountNumber.length) {
      const requestBody = {
        orgId: requestContext?.rootNodeId || '',
        siteId: requestContext?.context?.nodeId || '',
        startDate: startTimeForBreakdown,
        endDate: endTimeForBreakdown,
        utilityType: utilityType,
        isCashFlow: cashFlow,
        viewMode: 'graph',
        accounts: selectedAccountNumber
      };
      setGraphLoader(true);
      const result: IAny = await apiContext.getUtilityGraphData(requestBody);

      setGraphData(result);
      setGraphLoader(false);
    }
  };

  const [tableData, setTableData] = useState({} as any);
  const [tableLoader, setTableLoader] = useState<boolean>(true);

  useEffect(() => {
    setTableData({});
    getGraphData(selectedAccountNumber);
  }, [selectedAccountNumber]);

  useEffect(() => {
    if (tableData && Object.keys(tableData).length === 0) {
      setTableMainLoader(true);

      const formattedAcounts = formatAccountNumberArray(
        window?.ENV?.UTILITY_ACCOUNTS_PER_REQUEST
          ? window.ENV.UTILITY_ACCOUNTS_PER_REQUEST
          : 2
      );
      if (noAccountNumber) {
        setTableMainLoader(false);
        setTableLoader(false);
        setDisableLoader(false);
        apiContext.disableDateApply(false, t(d.SUMMARYTABLEAPIONGOING));
      } else {
        processData(formattedAcounts);
      }
    }
  }, [tableData]);

  const connectorConfigBillBreakdown = {
    name: 'utility-bill-breakdown',
    options: {
      startDate: startTimeForBreakdown,
      endDate: endTimeForBreakdown,
      utilityType: utilityType,
      isCashFlowView: requestContext.context.isCashFlow
        ? requestContext.context.isCashFlow
        : ''
    }
  };

  const connectorConfigAccountNumbers = {
    name: 'utility-bill-account-numbers',
    options: {
      startDate: startTimeForBreakdown,
      endDate: endTimeForBreakdown
    }
  };

  const connectorConfigBillBreakdownTable = {
    name: 'utility-bill-breakdown',
    options: {
      startDate: startTimeForBreakdown,
      endDate: endTimeForBreakdown,
      utilityType: utilityType,
      isCashFlowView: 'true'
    }
  };

  const [dataConnectorBillBreakdownTable] = useConnectorAndDynamicProps(
    {
      connectorConfig: connectorConfigBillBreakdownTable,
      requestContext,
      bearerToken
    },
    !props.emaasContext?.forPortfolio && utilityType !== UtilityType.Electric
  );

  // const [dataConnectorBillBreakdownTableForGas] = useConnectorAndDynamicProps({
  //   connectorConfig: connectorConfigBillBreakdownTableForGas,
  //   requestContext,
  //   bearerToken
  // });

  // const [dataConnectorBillBreakdownTableForWater] = useConnectorAndDynamicProps(
  //   {
  //     connectorConfig: connectorConfigBillBreakdownTableForWater,
  //     requestContext,
  //     bearerToken
  //   }
  // );

  // const [dataConnectorBillBreakdownTableForSewer] = useConnectorAndDynamicProps(
  //   {
  //     connectorConfig: connectorConfigBillBreakdownTableForSewer,
  //     requestContext,
  //     bearerToken
  //   }
  // );

  const [dataForAccounts, setDataForAccounts] = useState<any>([]);
  const [noAccountNumber, setNoAccountNumber] = useState<boolean>(false);

  const getAccountNumbers = async () => {
    apiContext.disableDateApply(true, t(d.SUMMARYTABLEAPIONGOING));
    setDisableLoader(true);
    const acnNumbers = await apiContext.getUtilityAccountNumbers();
    // setSelectedAccountNumber(acnNumbers);
    if (acnNumbers && acnNumbers.length === 0) {
      setNoAccountNumber(true);
    }
    setDataForAccounts(acnNumbers);
    apiContext.disableDateApply(false, '');
    setDisableLoader(false);
  };

  const [firstTimeLoad, setFirstTimeLoad] = useState<boolean>(true);
  useEffect(() => {
    getAccountNumbers();
    setTimeout(() => {
      setFirstTimeLoad(false);
    }, 1000);
  }, []);

  useEffect(() => {
    if (isMounted && hasRContextChangedForAdvnc(requestContext)) {
      if (!firstTimeLoad) {
        setNoAccountNumber(false);
        getAccountNumbers();
        setPrevRContextAdvnc(requestContext);
      }
    }
  }, [requestContext]);

  useEffect(() => {
    setIsOverview(apiContext.overViewElectricity);
    setIsMonthly(apiContext.isMonthlyElectricity);
    apiContext.basicAdvancedGrp
      ? setButtonGroup(apiContext.basicAdvancedGrp)
      : setButtonGroup([
          { content: t(d.BASIC), type: 'primary' },
          { content: t(d.ADVANCED), type: 'secondary' }
        ]);
    apiContext.monthlyBillingGrp
      ? setButtonGroupBilling(apiContext.monthlyBillingGrp)
      : setButtonGroupBilling([
          { content: t(d.MONTHLY), type: 'primary' },
          { content: t(d.BILLING), type: 'secondary' }
        ]);
  }, [
    apiContext.overViewElectricity,
    apiContext.isMonthlyElectricity,
    apiContext.basicAdvancedGrp,
    apiContext.monthlyBillingGrp
  ]);

  const performanceHeadingList = [
    t(d.ELECTRICITYPERFORMANCE),
    t(d.GASPERFORMANCEDETAILS),
    t(d.WaterPerformance),
    t(d.SEWERAGEPERFORMANCE)
  ];

  const [displayalert, setDisplayalert] = useState(true);
  const closealert = () => {
    setDisplayalert(false);
  };

  const [graphLoader, setGraphLoader] = useState<boolean>(true);
  const [tableMainLoader, setTableMainLoader] = useState<boolean>(true);
  const [disableLoader, setDisableLoader] = useState<boolean>(false);

  useEffect(() => {
    if (!firstTimeLoad) {
      setNoAccountNumber(false);
      getAccountNumbers();
      setGraphLoader(true);
      setTableLoader(true);
      setTableMainLoader(true);
      setDisplayalert(true);
    }
  }, [isMonthly]);

  const commonProps = {
    performanceHeading: performanceHeadingList[activeTab]
  };

  // let dConnectorForOtherUtilityType;

  // switch (utilityType) {
  //   case UtilityType.Gas:
  //     dConnectorForOtherUtilityType = dataConnectorBillBreakdownTableForGas;
  //     break;
  //   case UtilityType.Water:
  //     dConnectorForOtherUtilityType = dataConnectorBillBreakdownTableForWater;
  //     break;
  //   case UtilityType.Sewerage:
  //     dConnectorForOtherUtilityType = dataConnectorBillBreakdownTableForSewer;
  //     break;
  //   default:
  //     dConnectorForOtherUtilityType = dataConnectorBillBreakdownTableForGas;
  //     break;
  // }

  interface IBackControls {
    onBackarrowClick: () => void;
  }
  const BackControls = (props: IBackControls) => (
    <div className="emaas-utility-bill-viewDetails-kpi-card-title">
      <span className="utility-bill-backButton">
        <Icon
          root="common"
          name="arrow-left"
          size="medium"
          onClick={props.onBackarrowClick}
        />
      </span>
      {t(d.VIEW_OVERVIEW)}
    </div>
  );
  const isDetailedViewEnabled = [
    UtilityType.Electricity
    // UtilityType.Water
  ].includes(UtilityBillType || '');

  const getData = async (num: number, index: number) => {
    if (apiContext && apiContext.getTableData) {
      let cashFlow;
      if (isMonthly === false) {
        cashFlow = true;
      } else cashFlow = false;
      const requestBody = {
        startDate: requestContext.context.startTime,
        endDate: requestContext.context.endTime,
        utilityType: 'electric',
        isCashFlow: cashFlow,
        viewMode: 'summary',
        accounts: num
      };
      return apiContext?.getTableData(requestBody);
    }
  };

  const processData = async (acntNumber: any) => {
    apiContext.disableDateApply(true, t(d.SUMMARYTABLEAPIONGOING));
    setDisableLoader(true);
    for (let i = 0; i < acntNumber.length; i++) {
      const data: any = await getData(acntNumber[i], i);
      setTableData(data);
      if (i === 0) {
        setTableMainLoader(false);
      }
      if (i === acntNumber.length - 1) {
        setDisableLoader(false);
        apiContext.disableDateApply(false, '');
        setTableLoader(false);
      }
    }
  };

  const formatAccountNumberArray = (n: number) => {
    const finalarr = [];
    for (let i = 0; i < selectedAccountNumber.length; i += n) {
      finalarr.push(selectedAccountNumber.slice(i, i + n));
    }
    return finalarr;
  };

  const checkOverview = isOverview || !isOverview;
  return isOutOfRange ? (
    <Card>
      <Card.Content>
        <div>{t(d.UTILITYYEAROUTOFRANGE)}</div>
      </Card.Content>
    </Card>
  ) : (
    <div className="Basic-Advance-Parent">
      <div style={displayalert ? {} : { height: '3rem' }}>
        {props.emaasContext?.toggleUtilityBreakdownVisualization &&
          !props.emaasContext?.forPortfolio &&
          isDetailedViewEnabled &&
          checkOverview && [
            <div className="billing-monthly-parent">
              <div className="ToggleTab">
                <Tooltip
                  content={disableLoader ? t(d.SUMMARYTABLEAPIONGOING) : ''}
                  event="hover"
                  hoverable={true}
                  element={
                    <Button.Group className="ToggleTabButton">
                      {buttonGroup.map(
                        (buttonData: IButtongroup, index: number) => (
                          <Button
                            className="ToggleTab-basic"
                            disabled={disableLoader}
                            //type="primary"
                            key={index}
                            type={buttonData.type}
                            content={buttonData.content}
                            onClick={() => toggleButton(index, buttonData)}
                          />
                        )
                      )}
                    </Button.Group>
                  }
                />
              </div>
              {!isOverview && (
                <div className="ToggleTabBillingAdvanced">
                  <Tooltip
                    content={disableLoader ? t(d.SUMMARYTABLEAPIONGOING) : ''}
                    event="hover"
                    hoverable={true}
                    element={
                      <Button.Group className="ToggleTabButton">
                        {buttonGroupBilling.map(
                          (buttonData: IButtongroup, index: number) => (
                            <Button
                              className="ToggleTab-basic"
                              //type="primary"
                              key={index}
                              disabled={disableLoader}
                              type={buttonData.type}
                              content={buttonData.content}
                              onClick={() =>
                                toggleButtonForMonthlyBilling(index, buttonData)
                              }
                            />
                          )
                        )}
                      </Button.Group>
                    }
                  />

                  <div className="Billing-Monthly-Tooltip">
                    {displayalert === false ? (
                      <Tooltip
                        element={
                          <Icon root="common" name="badge-info" size="small" />
                        }
                        event="hover"
                        header={''}
                        content={
                          isMonthly
                            ? t(d.NOTIFICATIONBOXFORMONTHLY)
                            : t(d.NOTIFICATIONFORBILLINGPERIOD)
                        }
                        position="top right"
                        size="small"
                      />
                    ) : (
                      ''
                    )}
                  </div>
                </div>
              )}
            </div>,
            <div className="Electricity-Advanced">
              <div className="Electricity-Breakdown-Header">
                <div className="utility-bill-title">
                  {!isOverview &&
                    t(d.UTILITY_BILL_ANALYSIS, {
                      utilityName: t(getUtilityTitle(props.utilityType))
                    })}
                </div>
              </div>
              <div className="Account-Dropdown">
                {!isOverview && (
                  <UtilityAccountDropdown
                    // dataConnector={dataConnectorAccountNumbers}
                    accountNumberUpdateCallBack={(accnm: IAny) => {
                      // setDataForAccounts(accnm);

                      setSelectedAccountNumber(accnm);
                    }}
                    tableLoader={tableLoader}
                    graphLoader={graphLoader}
                    accountData={dataForAccounts}
                    selectedAccountNumber={selectedAccountNumber}
                  />
                )}
              </div>
            </div>,
            <div>
              {!isOverview && isMonthly == true
                ? displayalert && (
                    <Notification
                      hasIcon={true}
                      title="Monthly and Billing period view"
                      severity="information"
                      onCloseClick={() => {
                        closealert();
                      }}
                    >
                      {t(d.NOTIFICATIONBOXFORMONTHLY)}
                    </Notification>
                  )
                : !isOverview &&
                  displayalert && (
                    <Notification
                      hasIcon={true}
                      title="Billing period view"
                      severity="information"
                      onCloseClick={() => {
                        closealert();
                      }}
                    >
                      {t(d.NOTIFICATIONFORBILLINGPERIOD)}
                    </Notification>
                  )}
            </div>
          ]}
      </div>
      {/* {!isOverview && <BackControls onBackarrowClick={onBackarrowClick} />} */}
      {isOverview && (
        <>
          <UtilityBillPerformanceDetailComponent
            dataConnector={dataConnector}
            commonProps={commonProps}
            UtilityBillType={UtilityBillType} // once utilityType fixed from backend side then will remove this
            onViewDetailsClick={onViewDetailsClick}
            isOverview={isOverview}
          />
          <SavingsByMeterComponent
            dataConnector={dataConnector}
            UtilityBillType={UtilityBillType}
          />
          {!props.emaasContext?.forPortfolio &&
            utilityType !== UtilityType.Electric && (
              <UtilitySummaryWidget
                dataConnector={dataConnectorBillBreakdownTable}
                UtilityBillType={UtilityBillType}
                utilityType={utilityType}
                emaasContext={emaasContext}
              />
            )}
        </>
      )}
      {!isOverview && (
        <>
          {noAccountNumber ? (
            <div className="emaas-no-content-error">{t(d.NODATAAVAILABLE)}</div>
          ) : graphLoader ? (
            <Loader className="utility-performance-graph-loader" />
          ) : (
            <UtilityBillBreakdownChartWidgetTogglePerformance
              dataConnector={(() => {}) as IAny}
              graphData={graphData}
              utilityType={utilityType}
              isMonthly={isMonthly}
            />
          )}
          {!noAccountNumber && tableMainLoader ? (
            <Loader className="utility-performance-table-main-loader" />
          ) : (
            !noAccountNumber && (
              <UtilitySummaryWidgetPerformance
                dataConnector={(() => {}) as IAny}
                UtilityBillType={UtilityBillType}
                utilityType={utilityType}
                isMonthly={isMonthly}
                tableDataAsync={tableData}
                tableLoader={tableLoader}
              />
            )
          )}
        </>
      )}
    </div>
  );
};
export default UtilitySavingDetailHOCTogglePerformance;
